/**
 * "Let's Reroll" click/tap-to-copy.
 *
 * Used for the Discord handle in the header contact block — Discord has no invite URL here, so the
 * handle is shown as text and copying it is a convenience, not the only way to get it.
 *
 * Markup contract (see sections/header-group.json):
 *   <button type="button" class="lr-copy" data-lr-copy="THE-VALUE">
 *     <span class="lr-copy__text">THE-VALUE</span>
 *     <span class="lr-copy__hint" aria-hidden="true">Copy</span>
 *   </button>
 *
 * Deliberate choices:
 * - DELEGATED listener on document, not per-element binding. The markup lives inside a theme
 *   section setting, so it can be re-rendered by the theme editor at any time; a delegated handler
 *   keeps working without re-initialising. It also means the same snippet can be reused in the
 *   footer later with no extra JS.
 * - PROGRESSIVE ENHANCEMENT. The handle is real text inside the button, so if this script fails to
 *   load, is blocked, or the browser has no clipboard access, the value is still visible and can be
 *   selected and copied by hand. Nothing is hidden behind the copy action.
 * - TWO CLIPBOARD PATHS. navigator.clipboard requires a secure context and can still reject (for
 *   example when the document isn't focused). The execCommand fallback covers older browsers and
 *   those rejections. If both fail we select the text instead, so the user can copy manually
 *   rather than being told nothing happened.
 * - Feedback is announced to screen readers via a polite live region, not just a colour change.
 */
(function () {
  'use strict';

  var RESET_MS = 1600;
  var timers = new WeakMap();

  function announce(message) {
    var region = document.getElementById('lr-copy-live');
    if (!region) {
      region = document.createElement('span');
      region.id = 'lr-copy-live';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'lr-copy__live';
      document.body.appendChild(region);
    }
    /* Clearing first forces re-announcement when the same message repeats. */
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 30);
  }

  function selectText(el) {
    try {
      var range = document.createRange();
      range.selectNodeContents(el);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) { /* selection is a nicety; ignore failures */ }
  }

  function legacyCopy(value) {
    var ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    /* Off-screen rather than display:none — hidden elements can't be selected. */
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }

  function feedback(btn, ok) {
    var label = btn.getAttribute('data-lr-copied-label') || 'Copied';
    var hint = btn.querySelector('.lr-copy__hint');
    if (ok) {
      btn.classList.add('is-copied');
      if (hint) {
        if (!hint.dataset.original) hint.dataset.original = hint.textContent;
        hint.textContent = label;
      }
      announce(label);
      window.clearTimeout(timers.get(btn));
      timers.set(btn, window.setTimeout(function () {
        btn.classList.remove('is-copied');
        if (hint && hint.dataset.original) hint.textContent = hint.dataset.original;
      }, RESET_MS));
    } else {
      var text = btn.querySelector('.lr-copy__text');
      if (text) selectText(text);
      announce('Press Ctrl or Command + C to copy');
    }
  }

  function handle(btn) {
    var value = btn.getAttribute('data-lr-copy');
    if (!value) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(
        function () { feedback(btn, true); },
        function () { feedback(btn, legacyCopy(value)); }
      );
    } else {
      feedback(btn, legacyCopy(value));
    }
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-lr-copy]') : null;
    if (!btn) return;
    event.preventDefault();
    handle(btn);
  });
})();
